import React, { useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { userService } from "services/user/user.service"

interface TwoFactorManageModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

enum ManageAction {
  CHOOSE = "CHOOSE",
  DISABLE = "DISABLE",
  DISABLE_BACKUP = "DISABLE_BACKUP"
}

export const TwoFactorManageModal: React.FC<TwoFactorManageModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [action, setAction] = useState<ManageAction>(ManageAction.CHOOSE)
  const [code, setCode] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>("")

  const handleDisable = async () => {
    if (!code || code.length !== 6) {
      setError("Please enter a valid 6-digit code")
      return
    }

    try {
      setLoading(true)
      setError("")
      const result = await userService.disableTwoFactor(code)
      if (result) {
        toast.success("2FA disabled successfully")
        onSuccess()
        handleClose()
      }
    } catch (err: any) {
      setError(err?.message ?? "Invalid code")
      toast.error("Failed to disable 2FA")
    } finally {
      setLoading(false)
    }
  }

  const handleDisableByBackup = async () => {
    if (!code) {
      setError("Please enter a backup code")
      return
    }

    try {
      setLoading(true)
      setError("")
      const result = await userService.removeTwoFactorByBackup(code)
      if (result) {
        toast.success("2FA disabled successfully using backup code")
        onSuccess()
        handleClose()
      }
    } catch (err: any) {
      setError(err?.message ?? "Invalid backup code")
      toast.error("Failed to disable 2FA")
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setAction(ManageAction.CHOOSE)
    setCode("")
    setError("")
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-grey-dark rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-grey-dark dark:text-white">{t("auth.twoFa.manageTitle")}</h2>
          <button onClick={handleClose} className="text-grey-dark dark:text-white hover:text-red" disabled={loading}>
            ✕
          </button>
        </div>

        {action === ManageAction.CHOOSE && (
          <div className="space-y-3">
            <p className="text-grey-dark dark:text-white mb-4">{t("auth.twoFa.chooseMethod")}</p>

            <button
              onClick={() => setAction(ManageAction.DISABLE)}
              className="w-full px-4 py-3 text-left border border-grey rounded hover:bg-grey-light dark:hover:bg-grey-darker dark:text-white"
            >
              <div className="font-semibold text-black dark:text-grey-light">{t("auth.twoFa.useAuthenticator")}</div>
              <div className="text-sm text-grey-dark dark:text-grey-light">{t("auth.twoFa.useAuthenticatorDesc")}</div>
            </button>

            <button
              onClick={() => setAction(ManageAction.DISABLE_BACKUP)}
              className="w-full px-4 py-3 text-left border border-grey rounded hover:bg-grey-light dark:hover:bg-grey-darker dark:text-white"
            >
              <div className="font-semibold text-black dark:text-grey-light">{t("auth.twoFa.useBackupCode")}</div>
              <div className="text-sm text-grey-dark dark:text-grey-light">{t("auth.twoFa.useBackupCodeDesc")}</div>
            </button>
          </div>
        )}

        {action === ManageAction.DISABLE && (
          <div className="space-y-4">
            <p className="text-grey-dark dark:text-white">{t("auth.twoFa.enterAuthenticatorCode")}</p>

            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                setError("")
              }}
              placeholder="000000"
              className="w-full px-3 py-2 border rounded dark:bg-grey-darker dark:text-white dark:border-grey font-mono text-2xl text-center tracking-widest"
              maxLength={6}
              autoFocus
            />

            {error && <p className="text-red text-sm">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setAction(ManageAction.CHOOSE)
                  setCode("")
                  setError("")
                }}
                className="flex-1 px-4 py-2 border border-grey rounded hover:bg-grey-light dark:text-white text-black"
                disabled={loading}
              >
                {t("common.back")}
              </button>
              <button
                onClick={handleDisable}
                disabled={loading || code.length !== 6}
                className="flex-1 px-4 py-2 bg-red text-white rounded hover:bg-red-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("auth.twoFa.disabling") : t("auth.twoFa.disableTwoFa")}
              </button>
            </div>
          </div>
        )}

        {action === ManageAction.DISABLE_BACKUP && (
          <div className="space-y-4">
            <p className="text-grey-dark dark:text-white">{t("auth.twoFa.enterBackupCode")}</p>

            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase())
                setError("")
              }}
              placeholder="XXXX-XXXX-XXXX"
              className="w-full px-3 py-2 border rounded dark:bg-grey-darker dark:text-white dark:border-grey font-mono text-center"
              autoFocus
            />

            {error && <p className="text-red text-sm">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setAction(ManageAction.CHOOSE)
                  setCode("")
                  setError("")
                }}
                className="flex-1 px-4 py-2 border border-grey rounded hover:bg-grey-light dark:text-white text-black"
                disabled={loading}
              >
                {t("common.back")}
              </button>
              <button
                onClick={handleDisableByBackup}
                disabled={loading || !code}
                className="flex-1 px-4 py-2 bg-red text-white rounded hover:bg-red-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("auth.twoFa.disabling") : t("auth.twoFa.disableTwoFa")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
